import { FC, useCallback, useContext, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { object } from "yup";
import { Country } from "../../../../../components/forms/country";
import { TextField } from "../../../../../components/forms/text";
import { ConfigContext } from "../../../../../pages/token-onboarding/single/config-context";
import { validationFields } from "../../../../common/models/fields";
import { Field } from "../../internal/field";
import { FieldHook, RenderFuncProps } from "../common";

const useSchema = () => {
  const { t } = useTranslation("fields");

  const streetSchema = validationFields.street.useSchema();
  const addressLine2Schema = validationFields.street.useSchema();
  const citySchema = validationFields.city.useSchema();
  const stateSchema = validationFields.state.useSchema();
  const zipCodeSchema = validationFields.zip.useSchema();
  const countrySchema = validationFields.country.useSchema();

  return useMemo(
    () =>
      object()
        .shape({
          addressLine1: streetSchema.required(
            t("required-field", {
              ns: "errors",
              field: t("street"),
            })
          ),
          addressLine2: streetSchema.notRequired(),
          country: countrySchema.required(
            t("required-field", {
              ns: "errors",
              field: t("country"),
            })
          ),
          city: citySchema.required(
            t("required-field", {
              ns: "errors",
              field: t("city"),
            })
          ),
          state: stateSchema.when("country", {
            is: "US",
            then: (s) =>
              s.required(
                t("required-field", {
                  ns: "errors",
                  field: t("state"),
                })
              ),
            otherwise: (s) => s.notRequired(),
          }),
          zip: zipCodeSchema.required(
            t("required-field", {
              ns: "errors",
              field: t("zip"),
            })
          ),
        })
        .required(
          t("required-field", {
            ns: "errors",
            field: t("address"),
          })
        )
        .label(t("address")),
    [
      streetSchema,
      addressLine2Schema,
      citySchema,
      stateSchema,
      zipCodeSchema,
      countrySchema,
    ]
  );
};

const Elem: FC<RenderFuncProps> = ({ item }) => {
  const { t } = useTranslation("fields");

  const { config } = useContext(ConfigContext);
  const { control } = useFormContext();

  return (
    <>
      <div>{t("address-line-1")}</div>
      <Controller
        control={control}
        name={`${item.field}.addressLine1`}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            error={!!error}
            helperText={error?.message}
            fullWidth
            type="text"
            autoComplete="address-line1"
          />
        )}
      />
      <div>{t("address-line-2")}</div>
      <Controller
        control={control}
        name={`${item.field}.addressLine2`}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            error={!!error}
            helperText={error?.message}
            fullWidth
            type="text"
            autoComplete="address-line2"
          />
        )}
      />
      <div>{t("city")}</div>
      <Controller
        control={control}
        name={`${item.field}.city`}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            error={!!error}
            helperText={error?.message}
            fullWidth
            type="text"
            autoComplete="address-level2"
          />
        )}
      />
      <div>{t("state")}</div>
      <Controller
        control={control}
        name={`${item.field}.state`}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            error={!!error}
            helperText={error?.message}
            fullWidth
            type="text"
            autoComplete="address-level1"
          />
        )}
      />

      <div>{t("postal-code")}</div>
      <Controller
        control={control}
        name={`${item.field}.zip`}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            error={!!error}
            helperText={error?.message}
            fullWidth
            type="text"
            autoComplete="postal-code"
          />
        )}
      />
      <div>{t("country")}</div>
      <Controller
        control={control}
        name={`${item.field}.country`}
        render={({ field, fieldState: { error } }) => (
          <Country
            {...field}
            error={!!error}
            helperText={error?.message}
            exclude={config.configuration.prohibitedCountries}
            label={t("country")}
            fullWidth
          />
        )}
      />
    </>
  );
};

export const useAddressField: FieldHook = () => {
  const schema = useSchema();
  return useMemo(
    () => ({
      schema,
      elem: Elem,
      service: "ADDRESS",
    }),
    [schema]
  );
};
